import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from '../utils/webComponents';
import { 
  Close, 
  Add,
  CheckCircle
} from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const AddClassModal = ({ visible, onClose }) => {
  const { addClass } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [className, setClassName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#60A5FA');
  const [addedClasses, setAddedClasses] = useState([]);

  const classColors = [
    '#60A5FA', '#34D399', '#FBBF24', '#F87171', 
    '#A78BFA', '#22D3EE', '#86EFAC', '#94A3B8'
  ];

  const handleAddClass = () => {
    if (!className.trim()) return;

    const newClass = {
      name: className.trim(),
      color: selectedColor,
    };
    
    // Add to context
    addClass(newClass);
    
    // Add to local list to show what was added
    setAddedClasses([...addedClasses, newClass]);
    
    // Reset input
    setClassName('');
  };

  const handleClose = () => {
    setClassName('');
    setSelectedColor('#60A5FA');
    setAddedClasses([]);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddClass();
    }
  };

  if (!visible) return null;

  return (
    <View style={styles.overlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <View style={[styles.modal, { backgroundColor: theme.surface }]}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.text }]}>{t('onboarding.addClasses')}</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
          >
            <Close sx={{ fontSize: 24, color: theme.textMuted }} />
          </TouchableOpacity>
        </View>

        {/* Input Section */}
        <View style={styles.inputSection}>
          <TextInput
            style={[styles.input, { 
              color: theme.inputText, 
              borderColor: theme.inputBorder, 
              backgroundColor: theme.inputBackground 
            }]}
            value={className}
            onChangeText={setClassName}
            onKeyPress={handleKeyPress}
            placeholder={t('onboarding.className')}
            autoFocus
          />
          
          {/* Color Picker */}
          <View style={styles.colorPicker}>
            <Text style={[styles.colorLabel, { color: theme.textSecondary }]}>{t('onboarding.color')}:</Text>
            <View style={styles.colorOptions}>
              {classColors.map((color) => (
                <TouchableOpacity
                  key={color}
                  style={[
                    styles.colorOption,
                    { backgroundColor: color },
                    selectedColor === color && styles.colorOptionSelected
                  ]}
                  onPress={() => setSelectedColor(color)}
                >
                  {selectedColor === color && (
                    <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <TouchableOpacity
            style={[styles.addButton, { backgroundColor: theme.primary }]}
            onPress={handleAddClass}
            disabled={!className.trim()}
          >
            <Add sx={{ fontSize: 20, color: theme.buttonText }} />
            <Text style={[styles.addButtonText, { color: theme.buttonText }]}>{t('common.add')}</Text>
          </TouchableOpacity>
        </View>

        {/* Added Classes List */}
        {addedClasses.length > 0 && (
          <View style={[styles.addedSection, { borderTopColor: theme.border }]}>
            <Text style={[styles.addedTitle, { color: theme.text }]}>{t('onboarding.addedClasses')}:</Text>
            <View style={styles.addedList}>
              {addedClasses.map((cls, index) => (
                <View 
                  key={index} 
                  style={[styles.addedClassItem, { 
                    backgroundColor: theme.card, 
                    borderLeftColor: cls.color
                  }]}
                >
                  <View style={[styles.addedColorDot, { backgroundColor: cls.color }]} />
                  <Text style={[styles.addedClassName, { color: theme.text }]}>
                    {cls.name}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Done Button */}
        <TouchableOpacity
          style={[styles.doneButton, { backgroundColor: theme.success }]}
          onPress={handleClose}
        >
          <Text style={[styles.doneButtonText, { color: theme.buttonText }]}>{t('common.done')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    zIndex: 1000,
  },
  modal: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
  },
  closeButton: {
    padding: 4,
    cursor: 'pointer',
  },
  inputSection: {
    marginBottom: 24,
  },
  input: {
    width: '100%',
    fontSize: 16,
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 20,
  },
  colorPicker: {
    marginBottom: 20,
  },
  colorLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  colorOptions: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  colorOption: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    borderWidth: 2,
    borderColor: 'transparent',
    transition: 'transform 0.2s, border-color 0.2s',
  },
  colorOptionSelected: {
    borderColor: 'rgba(0, 0, 0, 0.3)',
    transform: 'scale(1.1)',
  },
  addButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    padding: 14,
    gap: 8,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  addedSection: {
    marginBottom: 24,
    paddingTop: 24,
    borderTopWidth: 1,
  },
  addedTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  addedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 200,
    overflowY: 'auto',
  },
  addedClassItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
  },
  addedColorDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  addedClassName: {
    fontSize: 16,
    fontWeight: '500',
  },
  doneButton: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  doneButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AddClassModal;
